import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { ProductsService } from 'src/app/services/product.service';
import {
  Product,
  getDefaultStockThresholds,
  getProductStockThresholds,
  StockThresholds,
  DEFAULT_STOCK_THRESHOLDS,
} from 'src/app/models/product.model';
import { MerchantType } from 'src/app/models/onboarding.model';
import type { ActivityType } from 'src/app/models/cluster.model';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';
import { ProductsEditorComponent } from 'src/app/shared/components/products-editor/products-editor.component';
import { MenuCardComponent } from 'src/app/shared/components/menu-card/menu-card.component';
import { CatalogTemplatePickerComponent } from 'src/app/components/catalog-template-picker/catalog-template-picker.component';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '../../../services/log.service';

interface ProductConfigRow extends Product {
  margin?: number;
  marginPercent?: number;
  hasError?: boolean;
  errorMessage?: string;
  hasWarning?: boolean;
  warningMessage?: string;
  stockRotation?: 'rapide' | 'moyenne' | 'lente';
  stockRotationDays?: number;
  riskType?: 'rupture' | 'surstock' | null;
}

interface CategoryGroup {
  category: string;
  products: ProductConfigRow[];
  expanded: boolean;
  totalValue: number;
}

@Component({
  selector: 'app-step-products-config',
  imports: [
    CommonModule,
    FormsModule,
    ProgressBarComponent,
    ProductsEditorComponent,
    MenuCardComponent,
  ],
  templateUrl: './step-products-config.component.html',
  styleUrls: ['./step-products-config.component.css'],
})
export class StepProductsConfigComponent implements OnInit {
  private log = inject(LogService);
  currentStep = 5;
  totalSteps = 6;

  products: ProductConfigRow[] = [];
  merchantType!: MerchantType;
  activityTypeStr: string = '';
  activityType: ActivityType | null = null;

  loading = false;
  saving = false;
  autoSaving = false;

  totalProducts = 0;
  validProducts = 0;
  totalRevenuePotential = 0;
  totalStockPurchaseValue = 0;
  totalStockSellValue = 0;

  // Filtres et groupement
  selectedCategory = 'all';
  categories: string[] = [];
  categoryGroups: CategoryGroup[] = [];
  viewMode: 'table' | 'grouped' = 'grouped';

  // Auto-save
  private autoSave$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  // Statistiques risques
  productsAtRisk = 0;

  // Produit sélectionné pour le panel détails
  selectedProductId: string | null = null;
  selectedProduct: ProductConfigRow | null = null;
  showSearchCatalog = false;
  showTemplatePicker = false;
  showMenuCard = false;
  businessName = '';

  // Personnalisation des seuils de stock (par produit)
  editingThresholds = false;
  tempThresholds: StockThresholds = { ...DEFAULT_STOCK_THRESHOLDS };

  // Unités disponibles (chargées depuis la DB)
  units: string[] = [];

  constructor(
    private onboardingStateService: OnboardingStateService,
    private productsService: ProductsService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.merchantType = this.onboardingStateService.getMerchantType()!;
    this.activityTypeStr = this.onboardingStateService.getActivityType() || '';
    // Charger l'activityType pour le catalogue en couches
    const formData = this.onboardingStateService.getFormData();
    this.activityType = formData?.activityType || null;
    this.businessName = formData?.businessName || '';
    this.loadProducts();
    this.setupAutoSave();
    this.loadUnits();
    this.setupTemplateListener();
  }

  setupAutoSave() {
    this.autoSave$
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.autoSaveProducts();
        this.cdr.markForCheck();
      });
  }

  loadUnits() {
    this.productsService
      .loadMeta()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Lecture directe du signal (compatible zoneless)
        this.units = this.productsService.units();
        this.cdr.markForCheck();
      });
  }

  async autoSaveProducts() {
    if (this.autoSaving) {
      // Queue another save after current one finishes
      this.autoSave$.next();
      return;
    }

    this.autoSaving = true;
    try {
      await this.productsService.updateProducts(this.products).toPromise();
      this.log.debug('✅ Auto-save réussi');
    } catch (error) {
      this.log.error('❌ Erreur auto-save:', error);
    } finally {
      this.autoSaving = false;
      this.cdr.markForCheck();
    }
  }

  async loadProducts() {
    this.loading = true;

    try {
      const selectedProducts = await this.productsService
        .getMyProducts()
        .toPromise();

      this.log.debug('📥 Produits reçus du backend:', selectedProducts?.length);

      if (!selectedProducts || selectedProducts.length === 0) {
        this.log.debug('Aucun produit trouvé - affichage du catalogue vide');
        this.products = [];
        this.loading = false;
        this.cdr.markForCheck();
        return;
      }

      // Trier par popularité et afficher tous les produits
      const sortedProducts = selectedProducts.sort(
        (a, b) => (b.popularity || 0) - (a.popularity || 0),
      );

      this.products = sortedProducts.map((p) => {
        // Appliquer les seuils par défaut si non définis
        const defaultThresholds = getDefaultStockThresholds(
          this.activityTypeStr,
          p.category || 'default',
        );

        return {
          ...p,
          purchasePrice: p.purchasePrice ?? p.defaultPurchasePrice ?? 0,
          sellPrice: p.sellPrice ?? p.defaultSellPrice ?? 0,
          quantity: p.quantity ?? p.defaultQuantity ?? 10,
          // Appliquer les seuils par défaut si le produit n'en a pas
          minStockThreshold:
            p.minStockThreshold !== undefined
              ? p.minStockThreshold
              : defaultThresholds.minStock,
          maxStockThreshold:
            p.maxStockThreshold !== undefined
              ? p.maxStockThreshold
              : defaultThresholds.maxStock,
          margin: 0,
          marginPercent: 0,
          hasError: false,
          errorMessage: '',
          riskType: null,
        };
      });

      this.log.debug('📦 Produits chargés:', this.products.length);
      this.log.debug('🔥 Premier produit:', {
        name: this.products[0]?.name,
        popularity: this.products[0]?.popularity,
        quantity: this.products[0]?.quantity,
      });

      // Debug: Afficher la distribution des popularités
      const popularityDistribution = this.products.reduce(
        (acc, p) => {
          const pop = p.popularity || 0;
          acc[pop] = (acc[pop] || 0) + 1;
          return acc;
        },
        {} as Record<number, number>,
      );
      this.log.debug('📊 Distribution popularité:', popularityDistribution);
      this.totalProducts = this.products.length;
      this.extractCategories();
      this.calculateAllMargins();
      this.detectRisks();
      this.calculateStats();
      this.groupByCategory();
    } catch (error) {
      this.log.error('Erreur chargement produits:', error);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  onPriceChange(product: ProductConfigRow) {
    this.calculateMargin(product);
    this.validateProduct(product);
    this.detectRisks();
    this.calculateStats();
    this.autoSave$.next();
  }

  onQuantityChange(product: ProductConfigRow) {
    this.detectRisks();
    this.calculateStats();
    this.autoSave$.next();
  }

  calculateMargin(product: ProductConfigRow) {
    const margin = product.sellPrice - product.purchasePrice;
    const marginPercent =
      product.sellPrice > 0
        ? Math.round((margin / product.sellPrice) * 100)
        : 0;

    product.margin = Number(margin.toFixed(2));
    product.marginPercent = marginPercent;
  }

  calculateAllMargins() {
    this.products.forEach((p) => this.calculateMargin(p));
  }

  validateProduct(product: ProductConfigRow) {
    product.hasError = false;
    product.errorMessage = '';
    product.hasWarning = false;
    product.warningMessage = '';

    if (product.purchasePrice < 0) {
      product.hasError = true;
      product.errorMessage = 'Prix achat invalide';
      return;
    }

    if (product.sellPrice < 0) {
      product.hasError = true;
      product.errorMessage = 'Prix vente invalide';
      return;
    }

    if (product.margin! < 0) {
      product.hasError = true;
      product.errorMessage = '⚠️ Marge négative';
      return;
    }

    if (product.marginPercent! < 20) {
      product.hasWarning = true;
      product.warningMessage = '⚠️ Marge faible (<20%)';
      // Ne pas bloquer la poursuite, juste un warning
    }

    if (product.quantity < 1) {
      product.hasError = true;
      product.errorMessage = '⚠️ Quantité min: 1';
      return;
    }
  }

  extractCategories() {
    const uniqueCategories = [...new Set(this.products.map((p) => p.category))];
    this.categories = ['all', ...uniqueCategories.filter((c) => c)];
  }

  /**
   * Détecte les risques de stock (rupture/surstock) basés sur les seuils du produit
   * Chaque produit a ses propres seuils (préréglés par défaut, ajustables)
   */
  detectRisks() {
    this.productsAtRisk = 0;

    this.products.forEach((p) => {
      const quantity = p.quantity || 0;

      // Récupérer les seuils du produit (ou les seuils par défaut du cluster)
      const thresholds: StockThresholds = getProductStockThresholds(
        p,
        this.activityTypeStr,
      );

      // Risque de rupture : quantité < seuil min
      if (quantity < thresholds.minStock) {
        p.riskType = 'rupture';
        this.productsAtRisk++;
      }
      // Risque de surstock : quantité > seuil max
      else if (quantity > thresholds.maxStock) {
        p.riskType = 'surstock';
        this.productsAtRisk++;
      } else {
        p.riskType = null;
      }
    });
  }

  selectProduct(productId: string) {
    this.selectedProductId =
      this.selectedProductId === productId ? null : productId;
  }

  groupByCategory() {
    const grouped = new Map<string, ProductConfigRow[]>();

    this.products.forEach((p) => {
      const cat = p.category || 'Autres';
      if (!grouped.has(cat)) {
        grouped.set(cat, []);
      }
      grouped.get(cat)!.push(p);
    });

    this.categoryGroups = Array.from(grouped.entries()).map(
      ([category, products]) => ({
        category,
        products,
        expanded: true,
        totalValue: products.reduce(
          (sum, p) => sum + p.sellPrice * p.quantity,
          0,
        ),
      }),
    );
  }

  toggleCategory(group: CategoryGroup) {
    group.expanded = !group.expanded;
  }

  setViewMode(mode: 'table' | 'grouped') {
    this.viewMode = mode;
  }

  // Helper methods pour le template
  getProductsByCategory(category: string): number {
    return this.products.filter((p) => p.category === category).length;
  }

  getValidProductsCount(products: ProductConfigRow[]): number {
    return products.filter((p) => !p.hasError).length;
  }

  get filteredProducts(): ProductConfigRow[] {
    if (this.selectedCategory === 'all') {
      return this.products;
    }
    return this.products.filter((p) => p.category === this.selectedCategory);
  }

  get filteredCategoryGroups(): CategoryGroup[] {
    if (this.selectedCategory === 'all') {
      return this.categoryGroups;
    }
    return this.categoryGroups.filter(
      (g) => g.category === this.selectedCategory,
    );
  }

  calculateStats() {
    this.validProducts = this.products.filter((p) => !p.hasError).length;
    this.totalRevenuePotential = this.products.reduce((sum, p) => {
      return sum + p.sellPrice * p.quantity;
    }, 0);
    this.totalStockPurchaseValue = this.products.reduce((sum, p) => {
      return sum + p.purchasePrice * p.quantity;
    }, 0);
    this.totalStockSellValue = this.products.reduce((sum, p) => {
      return sum + p.sellPrice * p.quantity;
    }, 0);
  }

  get canContinue(): boolean {
    return (
      this.products.length >= 3 &&
      this.products.every((p) => !p.hasError) &&
      this.validProducts === this.totalProducts
    );
  }

  async continue() {
    this.log.debug('ðŸ”µ continue() appelÃ©');
    this.log.debug('canContinue:', this.canContinue);
    this.log.debug('products.length:', this.products.length);
    this.log.debug('validProducts:', this.validProducts);
    this.log.debug('totalProducts:', this.totalProducts);
    this.log.debug(
      'products avec erreur:',
      this.products.filter((p) => p.hasError),
    );

    if (!this.canContinue) {
      this.log.debug('❌ canContinue est false, arrêt');
      return;
    }

    this.saving = true;
    this.log.debug('💾 Début sauvegarde...');

    try {
      const result = await this.productsService
        .updateProducts(this.products)
        .toPromise();
      this.log.debug('âœ… Sauvegarde rÃ©ussie:', result);

      this.onboardingStateService.updateFormData({
        products: this.products,
        productsConfigured: true,
      });

      this.navigateNextStep();
    } catch (error) {
      this.log.error('❌ Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  private navigateNextStep() {
    // Après les produits, aller au résumé (étape 6)
    this.router.navigate(['/onboarding/step/6']);
  }

  goBack() {
    // Retour vers l'étape contexte (step 4)
    if (this.merchantType === MerchantType.FIXED) {
      this.router.navigate(['/onboarding/step/4-fixed']);
    } else {
      this.router.navigate(['/onboarding/step/4-itinerant']);
    }
  }

  applyPriceMultiplier(multiplier: number) {
    this.products.forEach((p) => {
      if (p.defaultSellPrice) {
        p.sellPrice = Number((p.defaultSellPrice * multiplier).toFixed(2));
        this.onPriceChange(p);
      }
    });
  }

  resetToDefaults() {
    if (!confirm('Réinitialiser tous les produits aux valeurs par défaut ?')) {
      return;
    }

    this.products.forEach((p) => {
      p.purchasePrice = p.defaultPurchasePrice || 0;
      p.sellPrice = p.defaultSellPrice || 0;
      p.quantity = p.defaultQuantity || 10;
      this.onPriceChange(p);
    });
  }

  // ============= TEMPLATE PICKER =============

  onTemplateApplied(event: { templateName: string; productsCreated: number }) {
    this.showTemplatePicker = false;
    this.loadProducts();
  }

  private setupTemplateListener() {
    const handler = () => this.loadProducts();
    window.addEventListener('template-applied', handler);
    this.destroyRef.onDestroy(() =>
      window.removeEventListener('template-applied', handler),
    );
  }

  // ============= MÉTHODES POUR LE PANEL DÉTAILS =============

  onProductsChange(products: Product[]) {
    this.products = products.map((p) => {
      // Appliquer les seuils par défaut si non définis
      const defaultThresholds = getDefaultStockThresholds(
        this.activityTypeStr,
        p.category || 'default',
      );

      return {
        ...p,
        // Appliquer les seuils par défaut si le produit n'en a pas
        minStockThreshold:
          p.minStockThreshold !== undefined
            ? p.minStockThreshold
            : defaultThresholds.minStock,
        maxStockThreshold:
          p.maxStockThreshold !== undefined
            ? p.maxStockThreshold
            : defaultThresholds.maxStock,
        margin: 0,
        marginPercent: 0,
        hasError: false,
        errorMessage: '',
        hasWarning: false,
        warningMessage: '',
        riskType: null,
      };
    });
    this.totalProducts = this.products.length;
    this.calculateAllMargins();
    this.detectRisks();
    this.calculateStats();
    this.autoSave$.next();
  }

  onProductSelected(product: Product) {
    // MongoDB utilise _id, donc on cherche avec _id ou id
    const productId = (product as any)._id || product.id;
    this.selectedProduct =
      this.products.find(
        (p) => (p as any)._id === productId || p.id === productId,
      ) || null;

    // NB : on ne mute PAS popularity ici — la valeur d'origine (0-10) est
    // conservée telle quelle pour le backend. Le template peut afficher un
    // label descriptif via getPopularityLabel() si besoin.
  }

  onProductFieldChange() {
    if (this.selectedProduct) {
      this.calculateMargin(this.selectedProduct);
      this.validateProduct(this.selectedProduct);
      this.detectRisks();
      this.calculateStats();
      this.autoSave$.next();
    }
  }

  getMargin(product: ProductConfigRow): number {
    return product.sellPrice - product.purchasePrice;
  }

  getMarginPercent(product: ProductConfigRow): number {
    // Taux de marge = (Prix vente - Prix achat) / Prix achat × 100
    if (product.purchasePrice <= 0) return 0;
    return Math.round(
      ((product.sellPrice - product.purchasePrice) / product.purchasePrice) *
        100,
    );
  }

  removeProduct(product: ProductConfigRow) {
    this.products = this.products.filter((p) => p.id !== product.id);
    this.totalProducts = this.products.length;
    this.calculateStats();
    this.detectRisks();
    this.autoSave$.next();
  }

  /**
   * Confirme les modifications du produit et sauvegarde immédiatement en DB
   */
  async confirmProduct() {
    if (!this.selectedProduct) return;

    // Fermer le panel
    this.selectedProduct = null;
    this.editingThresholds = false;

    // Sauvegarder immédiatement en DB (sans attendre le debounce)
    this.saving = true;
    try {
      await this.productsService.updateProducts(this.products).toPromise();
      this.log.debug('✅ Produit sauvegardé en DB');
    } catch (error) {
      this.log.error('❌ Erreur sauvegarde:', error);
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Récupère les seuils de stock pour un produit donné
   * Priorité: seuils du produit > seuils cluster > seuils par défaut
   */
  getProductThresholds(product: ProductConfigRow): StockThresholds {
    return getProductStockThresholds(product, this.activityTypeStr);
  }

  /**
   * Ouvre l'éditeur de seuils pour la catégorie du produit sélectionné
   */
  openThresholdsEditor() {
    if (!this.selectedProduct) return;

    const category = this.selectedProduct.category || 'default';
    const currentThresholds = this.getProductThresholds(this.selectedProduct);

    // Copier les seuils actuels dans le formulaire temporaire
    this.tempThresholds = { ...currentThresholds };
    this.editingThresholds = true;
  }

  /**
   * Sauvegarde les seuils personnalisés sur le produit sélectionné
   */
  saveCustomThresholds() {
    if (!this.selectedProduct) return;

    // Validation
    if (this.tempThresholds.minStock >= this.tempThresholds.maxStock) {
      alert('Le seuil minimum doit être inférieur au seuil maximum');
      return;
    }

    // Sauvegarder les seuils directement sur le produit
    this.selectedProduct.minStockThreshold = this.tempThresholds.minStock;
    this.selectedProduct.maxStockThreshold = this.tempThresholds.maxStock;

    // Fermer l'éditeur et recalculer les risques
    this.editingThresholds = false;
    this.detectRisks();
    this.autoSave$.next();
  }

  /**
   * Annule l'édition des seuils
   */
  cancelThresholdsEdit() {
    this.editingThresholds = false;
  }

  /**
   * Réinitialise les seuils du produit aux valeurs par défaut du cluster
   */
  resetProductThresholds() {
    if (!this.selectedProduct) return;

    const category = this.selectedProduct.category || 'default';

    // Supprimer les seuils personnalisés du produit
    delete this.selectedProduct.minStockThreshold;
    delete this.selectedProduct.maxStockThreshold;

    // Mettre à jour le formulaire avec les valeurs par défaut du cluster
    this.tempThresholds = {
      ...getDefaultStockThresholds(this.activityTypeStr, category),
    };

    // Recalculer les risques
    this.detectRisks();
    this.autoSave$.next();
  }

  /**
   * Vérifie si le produit a des seuils personnalisés
   */
  hasCustomThresholds(product: ProductConfigRow): boolean {
    return (
      product.minStockThreshold !== undefined &&
      product.maxStockThreshold !== undefined
    );
  }

  /**
   * Calcule la position du stock en pourcentage sur la jauge
   * Les zones sont: rouge 25%, vert 50%, orange 25%
   * - Zone rouge (0-25%): stock de 0 à minStock
   * - Zone verte (25-75%): stock de minStock à maxStock
   * - Zone orange (75-100%): stock de maxStock à sliderMax
   */
  getStockGaugePosition(product: ProductConfigRow): number {
    const thresholds = this.getProductThresholds(product);
    const quantity = product.quantity || 0;
    const sliderMax = this.getStockSliderMax(product);

    if (quantity <= 0) return 0;
    if (quantity >= sliderMax) return 100;

    if (quantity < thresholds.minStock) {
      // Zone rouge (0-25%)
      return (quantity / thresholds.minStock) * 25;
    } else if (quantity < thresholds.maxStock) {
      // Zone verte (25-75%)
      const rangeSize = thresholds.maxStock - thresholds.minStock;
      const posInRange = quantity - thresholds.minStock;
      return 25 + (posInRange / rangeSize) * 50;
    } else {
      // Zone orange (75-100%)
      const rangeSize = sliderMax - thresholds.maxStock;
      const posInRange = quantity - thresholds.maxStock;
      return 75 + (posInRange / rangeSize) * 25;
    }
  }

  /**
   * Retourne le message de risque pour le produit
   */
  getRiskMessage(product: ProductConfigRow): string | null {
    if (!product.riskType) return null;

    const thresholds = this.getProductThresholds(product);

    if (product.riskType === 'rupture') {
      return `⚠️ Stock faible (< ${thresholds.minStock})`;
    } else if (product.riskType === 'surstock') {
      return `⚠️ Stock excessif (> ${thresholds.maxStock})`;
    }
    return null;
  }

  /**
   * Retourne le max du slider de stock basé sur les seuils
   * Permet de dépasser maxStock jusqu'à 1.5x
   */
  getStockSliderMax(product: ProductConfigRow): number {
    const thresholds = this.getProductThresholds(product);
    // Au moins maxStock * 1.5, mais minimum 50 pour les petits stocks
    return Math.max(Math.round(thresholds.maxStock * 1.5), 50);
  }

  /**
   * Calcule la largeur en % de chaque zone de la jauge
   * basé sur les seuils réels du produit
   */
  getZoneWidth(zone: 'red' | 'green' | 'orange'): number {
    if (!this.selectedProduct) return 33;

    const thresholds = this.getProductThresholds(this.selectedProduct);
    const maxGauge = this.getStockSliderMax(this.selectedProduct);

    if (maxGauge <= 0) return 33;

    const redWidth = (thresholds.minStock / maxGauge) * 100;
    const greenWidth =
      ((thresholds.maxStock - thresholds.minStock) / maxGauge) * 100;
    const orangeWidth = 100 - redWidth - greenWidth;

    switch (zone) {
      case 'red':
        return Math.max(redWidth, 5); // Minimum 5% pour visibilité
      case 'green':
        return Math.max(greenWidth, 10);
      case 'orange':
        return Math.max(orangeWidth, 5);
      default:
        return 33;
    }
  }

  // Gestion du slider custom
  isDragging = false;

  onGaugeMouseDown(event: MouseEvent) {
    this.isDragging = true;
    this.updateStockFromMouse(event);
  }

  onGaugeMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.updateStockFromMouse(event);
    }
  }

  onGaugeMouseUp() {
    this.isDragging = false;
  }

  onGaugeMouseLeave() {
    // On garde le drag actif même si on sort de la zone
  }

  onGaugeTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.updateStockFromTouch(event);
  }

  onGaugeTouchMove(event: TouchEvent) {
    if (this.isDragging) {
      event.preventDefault(); // Empêcher le scroll
      this.updateStockFromTouch(event);
    }
  }

  private updateStockFromTouch(event: TouchEvent) {
    if (!this.selectedProduct || !event.touches.length) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = event.touches[0].clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

    const stock = this.percentToStock(percent);
    this.selectedProduct.quantity = Math.round(stock);
    this.onProductFieldChange();
  }

  private updateStockFromMouse(event: MouseEvent) {
    if (!this.selectedProduct) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

    // Convertir le pourcentage en stock selon les zones
    const stock = this.percentToStock(percent);
    this.selectedProduct.quantity = Math.round(stock);
    this.onProductFieldChange();
  }

  /**
   * Convertit un pourcentage de position (0-100) en valeur de stock
   * Inverse de getStockGaugePosition - zones: rouge 25%, vert 50%, orange 25%
   */
  private percentToStock(percent: number): number {
    if (!this.selectedProduct) return 0;

    const thresholds = this.getProductThresholds(this.selectedProduct);
    const sliderMax = this.getStockSliderMax(this.selectedProduct);

    if (percent <= 0) return 0;
    if (percent >= 100) return sliderMax;

    if (percent < 25) {
      // Zone rouge (0-25%) -> stock de 0 à minStock
      return (percent / 25) * thresholds.minStock;
    } else if (percent < 75) {
      // Zone verte (25-75%) -> stock de minStock à maxStock
      const posInZone = (percent - 25) / 50;
      return (
        thresholds.minStock +
        posInZone * (thresholds.maxStock - thresholds.minStock)
      );
    } else {
      // Zone orange (75-100%) -> stock de maxStock à sliderMax
      const posInZone = (percent - 75) / 25;
      return (
        thresholds.maxStock + posInZone * (sliderMax - thresholds.maxStock)
      );
    }
  }
}
